import { DateFormatEnum } from '../enum/date-format.enum';

const moment = require('moment');

export class DateUtils {

    // ===============================
    // 1. BASIC DATE CREATION & CURRENT
    // ===============================

    static getToday(): Date {
        return DateUtils.clearTime(new Date());
    }

    static getNow(): Date {
        return new Date();
    }

    // ===============================
    // 2. DATE ARITHMETIC OPERATIONS
    // ===============================

    static addSeconds(date: Date, seconds: number): Date {
        return new Date(date.getTime() + seconds * 1000);
    }

    static addMinutes(date: Date, minutes: number): Date {
        return new Date(date.getTime() + minutes * 60000);
    }

    static addHours(date: Date, hours: number): Date {
        return new Date(date.getTime() + hours * 3600000);
    }

    static addDays(date: Date, days: number): Date {
        return new Date(date.getTime() + days * 86400000);
    }

    static addWeeks(date: Date, weeks: number): Date {
        return DateUtils.addDays(date, weeks * 7);
    }

    static addMonths(date: Date, months: number): Date {
        const newDate = new Date(date);
        newDate.setMonth(date.getMonth() + months);

        // Ayın son gününde olduğunda ve yeni ay daha kısa olduğunda ayarlamalar yapılır
        if (newDate.getDate() < date.getDate()) {
            newDate.setDate(0);
        }

        return newDate;
    }

    static addYears(date: Date, years: number): Date {
        const newDate = new Date(date);
        newDate.setFullYear(date.getFullYear() + years);

        // Yılın son gününde olduğunda ve şubat ayı gibi özel durumlar için ayarlamalar yapılır
        if (newDate.getMonth() < date.getMonth()) {
            newDate.setDate(0);
        }

        return newDate;
    }

    // ===============================
    // 3. PERIOD START/END OPERATIONS
    // ===============================

    static firstWeekDay(date: Date): Date {
        const day = date.getDay();
        const diff = date.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
        const newDate = new Date(date);
        newDate.setDate(diff);
        return newDate;
    }

    static lastWeekDay(date: Date): Date {
        const firstWeekDay = this.firstWeekDay(date);
        return DateUtils.addDays(firstWeekDay, 6);
    }

    static firstWeekDayOfMonth(date: Date): Date {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const dayOfWeek = firstDay.getDay();

        // Ayın ilk günü Pazartesi değilse, bir sonraki Pazartesi'ye ayarla
        const diff = dayOfWeek === 0 ? 1 : (dayOfWeek === 1 ? 0 : (8 - dayOfWeek));
        return new Date(year, month, firstDay.getDate() + diff);
    }

    static firstMonthDay(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), 1);
    }

    static lastMonthDay(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0);
    }

    static firstYearDay(date: Date): Date {
        return new Date(date.getFullYear(), 0, 1);
    }

    static lastYearDay(date: Date): Date {
        return new Date(date.getFullYear(), 12, 0);
    }

    // Time clearing methods
    static clearTime(date: Date): Date {
        const newDate = new Date(date);
        newDate.setHours(0, 0, 0, 0);
        return newDate;
    }

    static clearSecond(date: Date): Date {
        const newDate = new Date(date);
        newDate.setSeconds(0, 0);
        return newDate;
    }

    // Boolean checks
    static isToday(date: Date): boolean {
        const today = new Date();
        return DateUtils.isSameDate(date, today);
    }


    static isWeekend(date: Date): boolean {
        return date.getDay() === 0 || date.getDay() === 6;
    }

    static isSameDate(date1: Date, date2: Date): boolean {
        return date1 && date2 &&
            date1.getFullYear() === date2.getFullYear() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getDate() === date2.getDate();
    }

    static isAfter(date1: Date, date2: Date): boolean {
        return date1.getTime() > date2.getTime();
    }

    static isBefore(date1: Date, date2: Date): boolean {
        return date1.getTime() < date2.getTime();
    }

    static isAnotherMonth(date1: Date, date2: Date): boolean {
        return date2 && date1.getMonth() !== date2.getMonth();
    }

    // Utility methods
    static clone(date: Date): Date {
        return new Date(date.getTime());
    }

    static diffDays(date1: Date, date2: Date): number {
        return Math.ceil(Math.abs(date1.getTime() - date2.getTime()) / (1000 * 60 * 60 * 24));
    }

    static toFormat(date: Date, format: string): string {
        return moment(date, moment.ISO_8601).format(format);
    }

    static toJSON(date: Date): string {
        return moment(date, moment.ISO_8601).format(DateFormatEnum.DATE_TIME_SERILIAZE_FORMAT);
    }



    static getStartEndDate(dateRange: string): any {
        let returnValue: any = {};

        const DateRangeEnum = {
            TODAY: 'TODAY',
            YESTERDAY: 'YESTERDAY',
            THIS_WEEK: 'THIS_WEEK',
            LAST_WEEK: 'LAST_WEEK',
            TOMORROW: 'TOMORROW',
            THIS_MONTH: 'THIS_MONTH',
            LAST_MONTH: 'LAST_MONTH',
            NEXT_MONTH: 'NEXT_MONTH',
            THIS_YEAR: 'THIS_YEAR',
            LAST_YEAR: 'LAST_YEAR',
            NEXT_YEAR: 'NEXT_YEAR',
            FIRST_WEEK_OF_MONTH: 'FIRST_WEEK_OF_MONTH',
            SECOND_WEEK_OF_MONTH: 'SECOND_WEEK_OF_MONTH',
            THIRD_WEEK_OF_MONTH: 'THIRD_WEEK_OF_MONTH',
            FOURTH_WEEK_OF_MONTH: 'FOURTH_WEEK_OF_MONTH',
            LAST7DAY: 'LAST7DAY',
            LAST30DAY: 'LAST30DAY',
            LAST90DAY: 'LAST90DAY',
            ALL: 'ALL',
            CUSTOM: 'CUSTOM'
        };

        if (dateRange == DateRangeEnum.TODAY) {
            returnValue.startDate = DateUtils.getNow();
            returnValue.endDate = DateUtils.getNow();
            returnValue.isDateActive = true;
            returnValue.title = DateRangeEnum.TODAY;
        } else if (dateRange == DateRangeEnum.YESTERDAY) {
            returnValue.startDate = DateUtils.addDays(DateUtils.getNow(), -1);
            returnValue.endDate = DateUtils.addDays(DateUtils.getNow(), -1);
            returnValue.isDateActive = true;
            returnValue.title = DateRangeEnum.YESTERDAY;
        } else if (dateRange == DateRangeEnum.THIS_WEEK) {
            returnValue.startDate = DateUtils.firstWeekDay(DateUtils.getNow());
            returnValue.endDate = DateUtils.lastWeekDay(DateUtils.getNow());
            returnValue.isDateActive = true;
            returnValue.title = DateRangeEnum.THIS_WEEK;
        } else if (dateRange == DateRangeEnum.LAST_WEEK) {
            const lastWeek = DateUtils.addWeeks(DateUtils.getNow(), -1);
            returnValue.startDate = DateUtils.firstWeekDay(lastWeek);
            returnValue.endDate = DateUtils.lastWeekDay(lastWeek);
            returnValue.isDateActive = true;
            returnValue.title = DateRangeEnum.LAST_WEEK;
        } else if (dateRange == DateRangeEnum.TOMORROW) {
            returnValue.startDate = DateUtils.addDays(DateUtils.getNow(), 1);
            returnValue.endDate = DateUtils.addDays(DateUtils.getNow(), 1);
            returnValue.isDateActive = true;
            returnValue.title = DateRangeEnum.TOMORROW;
        } else if (dateRange == DateRangeEnum.THIS_MONTH) {
            returnValue.startDate = DateUtils.firstMonthDay(DateUtils.getNow());
            returnValue.endDate = DateUtils.lastMonthDay(DateUtils.getNow());
            returnValue.isDateActive = true;
            returnValue.title = DateRangeEnum.THIS_MONTH;
        } else if (dateRange == DateRangeEnum.LAST_MONTH) {
            const lastMonth = DateUtils.addMonths(DateUtils.getNow(), -1);
            returnValue.startDate = DateUtils.firstMonthDay(lastMonth);
            returnValue.endDate = DateUtils.lastMonthDay(lastMonth);
            returnValue.isDateActive = true;
            returnValue.title = DateRangeEnum.LAST_MONTH;
        } else if (dateRange == DateRangeEnum.NEXT_MONTH) {
            const nextMonth = DateUtils.addMonths(DateUtils.getNow(), 1);
            returnValue.startDate = DateUtils.firstMonthDay(nextMonth);
            returnValue.endDate = DateUtils.lastMonthDay(nextMonth);
            returnValue.isDateActive = true;
            returnValue.title = DateRangeEnum.NEXT_MONTH;
        } else if (dateRange == DateRangeEnum.THIS_YEAR) {
            returnValue.startDate = DateUtils.firstYearDay(DateUtils.getNow());
            returnValue.endDate = DateUtils.lastYearDay(DateUtils.getNow());
            returnValue.isDateActive = true;
            returnValue.title = DateRangeEnum.THIS_YEAR;
        } else if (dateRange == DateRangeEnum.LAST_YEAR) {
            const lastYear = DateUtils.addYears(DateUtils.getNow(), -1);
            returnValue.startDate = DateUtils.firstYearDay(lastYear);
            returnValue.endDate = DateUtils.lastYearDay(lastYear);
            returnValue.isDateActive = true;
            returnValue.title = DateRangeEnum.LAST_YEAR;
        } else if (dateRange == DateRangeEnum.NEXT_YEAR) {
            const nextYear = DateUtils.addYears(DateUtils.getNow(), 1);
            returnValue.startDate = DateUtils.firstYearDay(nextYear);
            returnValue.endDate = DateUtils.lastYearDay(nextYear);
            returnValue.isDateActive = true;
            returnValue.title = DateRangeEnum.NEXT_YEAR;
        } else if (dateRange == DateRangeEnum.FIRST_WEEK_OF_MONTH) {
            returnValue.startDate = DateUtils.firstWeekDayOfMonth(DateUtils.getNow());
            returnValue.endDate = DateUtils.lastWeekDay(returnValue.startDate);
            returnValue.isDateActive = true;
            returnValue.title = DateRangeEnum.FIRST_WEEK_OF_MONTH;
        } else if (dateRange == DateRangeEnum.SECOND_WEEK_OF_MONTH) {
            returnValue.startDate = DateUtils.addWeeks(DateUtils.firstWeekDayOfMonth(DateUtils.getNow()), 1);
            returnValue.endDate = DateUtils.lastWeekDay(returnValue.startDate);
            returnValue.isDateActive = true;
            returnValue.title = DateRangeEnum.SECOND_WEEK_OF_MONTH;
        } else if (dateRange == DateRangeEnum.THIRD_WEEK_OF_MONTH) {
            returnValue.startDate = DateUtils.addWeeks(DateUtils.firstWeekDayOfMonth(DateUtils.getNow()), 2);
            returnValue.endDate = DateUtils.lastWeekDay(returnValue.startDate);
            returnValue.isDateActive = true;
            returnValue.title = DateRangeEnum.THIRD_WEEK_OF_MONTH;
        } else if (dateRange == DateRangeEnum.FOURTH_WEEK_OF_MONTH) {
            returnValue.startDate = DateUtils.addWeeks(DateUtils.firstWeekDayOfMonth(DateUtils.getNow()), 3);
            returnValue.endDate = DateUtils.lastWeekDay(returnValue.startDate);
            returnValue.isDateActive = true;
            returnValue.title = DateRangeEnum.FOURTH_WEEK_OF_MONTH;
        } else if (dateRange == DateRangeEnum.LAST7DAY) {
            returnValue.startDate = DateUtils.addDays(DateUtils.getNow(), -6);
            returnValue.endDate = DateUtils.getNow();
            returnValue.isDateActive = true;
            returnValue.title = DateRangeEnum.LAST7DAY;
        } else if (dateRange == DateRangeEnum.LAST30DAY) {
            returnValue.startDate = DateUtils.addDays(DateUtils.getNow(), -29);
            returnValue.endDate = DateUtils.getNow();
            returnValue.isDateActive = true;
            returnValue.title = DateRangeEnum.LAST30DAY;
        } else if (dateRange == DateRangeEnum.LAST90DAY) {
            returnValue.startDate = DateUtils.addDays(DateUtils.getNow(), -89);
            returnValue.endDate = DateUtils.getNow();
            returnValue.isDateActive = true;
            returnValue.title = DateRangeEnum.LAST90DAY;
        } else if (dateRange == DateRangeEnum.ALL) {
            returnValue.startDate = new Date(2000, 1, 1);
            returnValue.endDate = DateUtils.lastYearDay(DateUtils.addYears(DateUtils.getNow(), 5));
            returnValue.isDateActive = false;
            returnValue.title = DateRangeEnum.ALL;
        } else if (dateRange == DateRangeEnum.CUSTOM) {
            returnValue.isDateActive = true;
            returnValue.title = "";
        }

        if (returnValue.isDateActive && returnValue.startDate && returnValue.endDate) {
            returnValue.startDate = DateUtils.clearTime(returnValue.startDate);
            returnValue.endDate = DateUtils.clearTime(returnValue.endDate);
        }

        return returnValue;
    }

    static areSameDay(date1: Date, date2: Date): boolean {
        return DateUtils.isSameDate(date1, date2);
    }

    static toDate(dateStr: string | Date): Date | null {
        if (!dateStr) {
            return null;
        }
        if (dateStr instanceof Date) {
            return dateStr;
        }
        return new Date(dateStr);
    }
} 